import { com } from '@eidu/web'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import getPermissionGrant from './api/entity/permission/getPermissionGrant'
import getPermissionGrants from './api/entity/permission/getPermissionGrants'
import { PermissionGrantDTO } from '../domain/permission/PermissionGrantDTO'
import { permissionGrantKeys } from './queryKeys'
import PermissionGrantId = com.eidu.entity.permission.PermissionGrantId

export type PermissionGrantsPage = {
  rows: readonly PermissionGrantDTO[]
  totalCount: number
}

export const fetchPermissionGrantsPage = async (pageIndex: number, pageSize: number): Promise<PermissionGrantsPage> => {
  const response = await getPermissionGrants({ pageIndex, pageSize })
  return {
    rows: response.documents.asJsReadonlyArrayView(),
    totalCount: response.totalCount,
  }
}

export const usePermissionGrantsPage = (pageIndex: number, pageSize: number) => {
  const query = useQuery({
    queryKey: permissionGrantKeys.page(pageIndex, pageSize),
    queryFn: () => fetchPermissionGrantsPage(pageIndex, pageSize),
    placeholderData: keepPreviousData,
  })
  return {
    rows: query.data?.rows,
    totalCount: query.data?.totalCount,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
  }
}

export const fetchPermissionGrant = async (id: PermissionGrantId): Promise<PermissionGrantDTO | null> =>
  (await getPermissionGrant({ id })) ?? null

export const usePermissionGrant = (id: PermissionGrantId) => {
  const query = useQuery({
    queryKey: permissionGrantKeys.detail(id),
    queryFn: () => fetchPermissionGrant(id),
  })
  return {
    permissionGrant: query.data,
    isLoading: query.isLoading,
    error: query.error,
  }
}

export const useFetchPermissionGrant = () => {
  const queryClient = useQueryClient()
  return useCallback(
    (id: PermissionGrantId) =>
      queryClient.fetchQuery({
        queryKey: permissionGrantKeys.detail(id),
        queryFn: () => fetchPermissionGrant(id),
      }),
    [queryClient]
  )
}
